import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogService } from '@services/dialog.service';
import { Study } from '@models/study.model';
import { StudyDetailService } from '@services/study-detail.service';
import { MasterMatrixService } from '@services/mastermatrix.service';
import { AcknowledgeMessage, RouterLink } from '../../../../shared/_constants';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { StudyAnalysisSteps, AnalysisTabId } from '../../../_study_constants';
import { ConfigService } from '@services/config.service';
import { takeUntil } from 'rxjs/operators';
import { InternetReviewService } from '@services/internet-review.service';
import { FinancialReviewService } from '@services/financial-review.service';
import { BdMatrixService } from '@services/bd-matrix.service';

@Component({
  selector: 'app-import-matrix-modal',
  templateUrl: './import-matrix-modal.component.html',
  styleUrls: ['./import-matrix-modal.component.css'],
})
export class ImportMatrixModalComponent implements OnInit, OnDestroy {
  blockui = false;
  fileExtension = 'xls,xlsx';
  study: Study;
  files: any;
  maxFiles: string;
  maxSize: string;
  private _success = new Subject<string>();
  message = '';
  type = 'success';
  studyAnalysisSteps: StudyAnalysisSteps;
  title: string;

  constructor(
    private activeModal: NgbActiveModal,
    private studyDetailService: StudyDetailService,
    private dialogService: DialogService,
    private masterMatrixService: MasterMatrixService,
    private internetReviewService: InternetReviewService,
    private configService: ConfigService,
    private financialReviewService: FinancialReviewService,
    private bdMatrixService: BdMatrixService,
  ) {}

  private destroy$ = new Subject();

  ngOnInit() {
    this.configService
      .getConfigValue(ConfigService.UPLOADFILESETTING_SECTION + ':' + ConfigService.DEFAULT_MAX_FILELENGTH)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.maxFiles = val ? val : '1';
      });
    this.configService
      .getConfigValue(ConfigService.UPLOADFILESETTING_SECTION + ':' + ConfigService.DEFAULT_MAX_FILESIZE_IN_MB)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.maxSize = val ? val : '5';
      });
    this.study = this.studyDetailService.study;
    this._success.subscribe((message) => (this.message = message));
    this._success.pipe(debounceTime(8000)).subscribe(() => (this.message = ''));
  }

  close(): void {
    this.activeModal.close(null);
  }

  upload(file) {
    if (file) {
      this.files = file;
    }
  }

  async import() {
    try {
      const file = this.files;

      this.blockui = true;

      if (!file || file.length === 0) {
        this._success.next('Please upload the file to begin import process.');
        this.type = 'danger';
        return;
      }

      if (this.study && this.study.studyId) {
        const formData = new FormData();
        for (const _file of file) {
          formData.append('file', _file);
          formData.append('fileName', _file.name);
        }
        formData.append('studyId', this.study.studyId.toString());

        const studySearchStrategyId = this.study.searchStrategies.find((s) => s.isActive === true).studySearchStrategyId;
        formData.append('studySearchStrategyId', studySearchStrategyId.toString());

        const url = ` ${RouterLink.MyStudies}/${this.study.studyId}/${AnalysisTabId}/${this.studyAnalysisSteps}`;

        switch (this.studyAnalysisSteps) {
          case StudyAnalysisSteps.MasterMatrix:
            this.masterMatrixService.importMatrix(formData, url, this.study.studyId);
            break;
          case StudyAnalysisSteps.InternetReview:
            this.internetReviewService.importMatrix(formData, url, this.study.studyId);
            break;
          case StudyAnalysisSteps.FinancialReview:
            this.financialReviewService.importMatrix(formData, url, this.study.studyId);
            break;
          case StudyAnalysisSteps.BDReview:
            this.bdMatrixService.importMatrix(formData, url, this.study.studyId);
            break;
        }

        await this.dialogService.acknowledge('Processing Report ...', AcknowledgeMessage.UploadFileMessage, 'OK');

        this.close();
      }
    } catch (err) {
      this.blockui = false;
    } finally {
      this.blockui = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
